import {
	BuildingOffice2Icon,
	CalendarDaysIcon,
	ClockIcon,
	FlagIcon,
	MapPinIcon,
	PhoneIcon,
	PlayIcon,
	UserIcon,
} from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import NavigateButton from '@/pages/centrumSzkolen/common/NavigateButton'
import { buildTrainingLocation } from '@/pages/centrumSzkolen/Szkolenia/common/buildTrainingLocation'
import { formatLessonHoursLabel } from './buildMyProjectCalendarPayload'

const callButtonClassName = 'inline-flex items-center justify-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-xs transition hover:bg-emerald-500'

const formatDate = value => (value ? `${value}`.substring(0, 10) : '—')

const formatStartTime = value => {
	const match = `${value || ''}`.trim().match(/^(\d{1,2}):(\d{2})/)
	if (!match) return ''
	return `${match[1].padStart(2, '0')}:${match[2]}`
}

const formatGoogleLocation = step => {
	const built = buildTrainingLocation({
		miasto: step?.miasto,
		ulica: step?.ulica || step?.adres,
		kod_pocztowy: step?.kod_pocztowy,
		location: step?.location,
	})

	return built || '—'
}

const normalizePhoneHref = phone => {
	const raw = `${phone || ''}`.trim()
	if (!raw) return ''
	return `tel:${raw.replace(/[^\d+]/g, '')}`
}

const DetailRow = ({ icon: Icon, label, children }) => (
	<div className="flex items-start gap-3">
		<Icon className="mt-0.5 size-5 shrink-0 text-indigo-500" aria-hidden="true" />
		<div className="min-w-0">
			<dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
			<dd className="mt-0.5 text-gray-900">{children}</dd>
		</div>
	</div>
)

const PositionMetaRow = ({ icon: Icon, label, value }) => (
	<div className="flex items-center gap-2">
		<Icon className="size-4 shrink-0 text-indigo-500" aria-hidden="true" />
		<span className="text-gray-500">{label}</span>
		<span className="text-gray-900">{value || '—'}</span>
	</div>
)

const buildStepNavigatePayload = step => ({
	g_lat: step?.g_lat,
	g_long: step?.g_long,
})

export default function MyProjectStepsSection({ steps = [] }) {
	const { t } = useTranslation()
	const rows = Array.isArray(steps) ? steps : []

	return (
		<div className="mt-6 rounded-md border border-gray-200 bg-white px-5 py-4 shadow-sm">
			<div>
				<h2 className="text-sm font-semibold text-gray-900">
					{t('myProjects.details.sections.mySteps', { defaultValue: 'Etapy' })}
				</h2>
				<p className="mt-1 text-sm text-gray-500">
					{t('myProjects.details.steps.description', {
						defaultValue: 'Wszystkie etapy i pozycje projektu.',
					})}
				</p>
			</div>

			<div className="mt-4 space-y-4">
				{rows.length ? rows.map((step, index) => {
					const key = step._id || `step-${index}`
					const navigatePayload = buildStepNavigatePayload(step)
					const certificationNames = Array.isArray(step.certificationNames)
						? step.certificationNames
						: []
					const pozycje = Array.isArray(step.pozycje) ? step.pozycje : []
					const locationLabel = formatGoogleLocation(step)
					const contact = step.osoba_kontaktowa || {}
					const contactName = `${contact.nazwisko || ''}`.trim()
					const contactRole = `${contact.funkcja || ''}`.trim()
					const contactPhone = `${contact.telefon || ''}`.trim()
					const hasContact = Boolean(contactName || contactRole || contactPhone)
					const callHref = normalizePhoneHref(contactPhone)

					return (
						<div
							key={key}
							className="rounded-md border border-gray-200 bg-gray-50 px-4 py-4"
						>
							<h3 className="text-sm font-semibold text-gray-900">
								{t('projects.details.steps.stepLabel', {
									defaultValue: 'Etap {{number}}',
									number: index + 1,
								})}
							</h3>

							<div className="mt-4 grid grid-cols-1 gap-6 xl:grid-cols-2">
								<div className="rounded-md border border-gray-200 bg-white px-4 py-3">
									<h4 className="text-sm font-semibold text-gray-900">
										{t('trainings.details.sections.schedule')}
									</h4>
									<dl className="mt-3 flex flex-col gap-3">
										<DetailRow
											icon={BuildingOffice2Icon}
											label={t('projects.list.table.placowkaName', { defaultValue: 'Placówka' })}
										>
											{step.placowkaName || '—'}
										</DetailRow>
										<DetailRow icon={MapPinIcon} label={t('trainings.form.fields.location')}>
											{locationLabel}
										</DetailRow>
										<DetailRow icon={CalendarDaysIcon} label={t('projects.form.fields.stepDate', { defaultValue: 'Data etapu' })}>
											{formatDate(step.data_rozpoczecia)}
										</DetailRow>
									</dl>

									<div className="mt-5 flex flex-wrap justify-center gap-3">
										<NavigateButton training={navigatePayload} />
									</div>
								</div>

								<div className="rounded-md border border-gray-200 bg-white px-4 py-3">
									<h4 className="text-sm font-semibold text-gray-900">
										{t('trainings.details.sections.certifications')}
									</h4>
									<div className="mt-3 flex flex-wrap gap-2">
										{certificationNames.length ? certificationNames.map(name => (
											<span
												key={name}
												className="inline-flex items-center rounded-full border border-indigo-100 bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700"
											>
												{name}
											</span>
										)) : (
											<span className="text-sm text-gray-500">
												{t('trainings.details.emptyCertifications')}
											</span>
										)}
									</div>

									<div className="mt-4 border-t border-gray-100 pt-4">
										<h5 className="text-sm font-semibold text-gray-900">
											{t('projects.form.contactPerson.title', { defaultValue: 'Osoba kontaktowa' })}
										</h5>
										{hasContact ? (
											<div className="mt-3 flex items-start gap-3">
												<div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">
													<UserIcon className="size-5" aria-hidden="true" />
												</div>
												<div className="min-w-0 flex-1">
													<p className="text-sm font-semibold text-gray-900">
														{contactName || '—'}
													</p>
													{contactRole ? (
														<p className="mt-0.5 text-sm text-gray-500">{contactRole}</p>
													) : null}
													{contactPhone ? (
														<div className="mt-3 flex flex-wrap items-center gap-3">
															<a
																href={callHref}
																className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-900 hover:text-indigo-600"
															>
																<PhoneIcon className="size-4 text-gray-400" aria-hidden="true" />
																{contactPhone}
															</a>
															<a href={callHref} className={callButtonClassName}>
																<PhoneIcon className="size-3.5" aria-hidden="true" />
																{t('customers.details.actions.call', { defaultValue: 'Zadzwoń' })}
															</a>
														</div>
													) : null}
												</div>
											</div>
										) : (
											<p className="mt-2 text-sm text-gray-500">
												{t('myProjects.details.steps.emptyContact', {
													defaultValue: 'Brak osoby kontaktowej.',
												})}
											</p>
										)}
									</div>

									{step.notatki ? (
										<div className="mt-4 border-t border-gray-100 pt-3">
											<p className="text-xs font-medium uppercase tracking-wide text-gray-500">
												{t('projects.form.fields.stepNotes', { defaultValue: 'Notatki' })}
											</p>
											<p className="mt-1 whitespace-pre-line text-sm text-gray-600">
												{step.notatki}
											</p>
										</div>
									) : null}
								</div>
							</div>

							<div className="mt-4 space-y-2">
								<h4 className="text-xs font-medium uppercase tracking-wide text-gray-500">
									{t('projects.form.positionsSection.title', { defaultValue: 'Pozycje' })}
								</h4>
								{pozycje.length ? pozycje.map((pozycja, pozycjaIndex) => {
									const hoursLabel = formatLessonHoursLabel(pozycja.ilosc_godzin)
									const startTime = formatStartTime(pozycja.start_time)
									const endTime = formatStartTime(pozycja.end_time)
									return (
										<div
											key={pozycja._id || `pozycja-${pozycjaIndex}`}
											className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
										>
											<div className="font-medium text-gray-900">{pozycja.value || '—'}</div>
											<div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
												<PositionMetaRow
													icon={UserIcon}
													label={t('projects.list.table.trainerName', { defaultValue: 'Trener' })}
													value={pozycja.trainerName}
												/>
												<PositionMetaRow
													icon={PlayIcon}
													label={t('projects.form.positionsSection.startTime', { defaultValue: 'Godzina startu' })}
													value={startTime}
												/>
												<PositionMetaRow
													icon={FlagIcon}
													label={t('projects.form.positionsSection.endTime', { defaultValue: 'Godzina zakończenia' })}
													value={endTime}
												/>
												<PositionMetaRow
													icon={ClockIcon}
													label={t('myProjects.details.steps.positionLength', { defaultValue: 'Długość' })}
													value={hoursLabel}
												/>
											</div>
											{pozycja.uwagi ? (
												<p className="mt-1 whitespace-pre-line text-gray-500">{pozycja.uwagi}</p>
											) : null}
										</div>
									)
								}) : (
									<p className="text-sm text-gray-500">
										{t('projects.form.positionsSection.empty', {
											defaultValue: 'Brak pozycji. Dodaj pierwszą pozycję etapu.',
										})}
									</p>
								)}
							</div>
						</div>
					)
				}) : (
					<div className="rounded-md border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500">
						{t('myProjects.details.steps.empty', {
							defaultValue: 'Brak etapów.',
						})}
					</div>
				)}
			</div>
		</div>
	)
}
